import { Link, useNavigate } from "react-router-dom";
import { Loader2, ShoppingCart, Trash2, Globe, ArrowRight, Plus, Minus } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/hooks/use-toast";
import { ApiError } from "@/lib/api";
import {
  useCart,
  useRemoveCartItem,
  useUpdateCartItem,
  useClearCart
} from "@/hooks/useCart";
import type { BillingCycle, CartItemDto } from "@/types/api";

const CYCLES: BillingCycle[] = ["monthly", "quarterly", "annually"];

export function CartPage() {
  const { data: cart, isLoading } = useCart();
  const removeItem = useRemoveCartItem();
  const updateItem = useUpdateCartItem();
  const clearCart = useClearCart();
  const navigate = useNavigate();
  const { toast } = useToast();

  function onError(err: unknown) {
    toast({
      title: "Couldn't update cart",
      description: err instanceof ApiError ? err.message : "Please try again.",
      variant: "destructive"
    });
  }

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-40" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }

  const items = cart?.items ?? [];

  if (items.length === 0) {
    return (
      <div className="mx-auto max-w-md py-16 text-center">
        <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-muted text-muted-foreground">
          <ShoppingCart className="h-7 w-7" />
        </div>
        <h1 className="font-display text-2xl font-bold">Your cart is empty</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Browse plans and add one with a domain to get started.
        </p>
        <Button asChild className="mt-5">
          <Link to="/plans">Browse plans <ArrowRight className="h-4 w-4" /></Link>
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <h1 className="font-display text-2xl font-bold tracking-tight">Your cart</h1>
        <Button
          variant="ghost"
          size="sm"
          className="text-muted-foreground"
          disabled={clearCart.isPending}
          onClick={() => clearCart.mutate(undefined, { onError })}
        >
          Clear cart
        </Button>
      </div>

      <div className="grid gap-6 lg:grid-cols-[1fr_320px]">
        <div className="space-y-3">
          {items.map((item) => (
            <CartRow
              key={item.id}
              item={item}
              busy={updateItem.isPending || removeItem.isPending}
              onQty={(q) =>
                updateItem.mutate({ id: item.id, quantity: q }, { onError })
              }
              onCycle={(c) =>
                updateItem.mutate({ id: item.id, billing_cycle: c }, { onError })
              }
              onRemove={() => removeItem.mutate(item.id, { onError })}
            />
          ))}
        </div>

        <aside className="lg:sticky lg:top-6 h-fit">
          <Card>
            <CardContent className="space-y-4 py-5">
              <p className="font-mono text-[11px] uppercase tracking-wider text-brand-accentDark">
                Order summary
              </p>
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">
                  Subtotal ({cart?.item_count} item{cart?.item_count === 1 ? "" : "s"})
                </span>
                <span className="font-medium">{money(cart?.subtotal ?? 0)}</span>
              </div>
              <div className="border-t border-border pt-3">
                <div className="flex items-center justify-between">
                  <span className="font-semibold">Total today</span>
                  <span className="font-display text-lg font-bold">
                    {money(cart?.subtotal ?? 0)}
                  </span>
                </div>
              </div>
              <Button
                size="lg"
                className="w-full"
                onClick={() => navigate("/checkout")}
              >
                Proceed to checkout <ArrowRight className="h-4 w-4" />
              </Button>
              <p className="text-center text-xs text-muted-foreground">
                Secure card payment via Stripe.
              </p>
            </CardContent>
          </Card>
        </aside>
      </div>
    </div>
  );
}

function CartRow({
  item,
  busy,
  onQty,
  onCycle,
  onRemove
}: {
  item: CartItemDto;
  busy: boolean;
  onQty: (q: number) => void;
  onCycle: (c: BillingCycle) => void;
  onRemove: () => void;
}) {
  return (
    <Card>
      <CardContent className="flex flex-col gap-4 py-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <p className="font-semibold">{item.name}</p>
            {item.plan_type && (
              <span className="rounded-full bg-muted px-2 py-0.5 text-[11px] capitalize text-muted-foreground">
                {item.plan_type}
              </span>
            )}
          </div>
          {item.domains.length > 0 && (
            <div className="mt-1.5 space-y-1">
              {item.domains.map((d) => (
                <div key={d.domain_name} className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Globe className="h-3.5 w-3.5 shrink-0" />
                  <span className="font-medium text-foreground">{d.domain_name}</span>
                  <span className="text-xs capitalize">
                    · {d.mode === "UseExisting" ? "nameservers" : d.mode.toLowerCase()}
                  </span>
                  <span className="text-xs">
                    · {d.registration_price > 0 ? `${money(d.registration_price)}/yr` : "free"}
                  </span>
                </div>
              ))}
            </div>
          )}
          <div className="mt-2 flex flex-wrap items-center gap-2">
            {CYCLES.map((c) => (
              <button
                key={c}
                onClick={() => c !== item.billing_cycle && onCycle(c)}
                disabled={busy}
                className={`rounded-full px-2.5 py-1 text-xs capitalize transition ${
                  c === item.billing_cycle
                    ? "bg-primary text-primary-foreground"
                    : "bg-muted text-muted-foreground hover:bg-muted/70"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        </div>

        <div className="flex items-center gap-4">
          {item.item_type === "AddOn" && (
            <div className="flex items-center gap-1.5">
              <Button
                variant="outline"
                size="icon"
                className="h-7 w-7"
                disabled={busy || item.quantity <= 1}
                onClick={() => onQty(item.quantity - 1)}
              >
                <Minus className="h-3.5 w-3.5" />
              </Button>
              <span className="w-6 text-center text-sm font-medium">{item.quantity}</span>
              <Button
                variant="outline"
                size="icon"
                className="h-7 w-7"
                disabled={busy}
                onClick={() => onQty(item.quantity + 1)}
              >
                <Plus className="h-3.5 w-3.5" />
              </Button>
            </div>
          )}

          <div className="w-24 text-right">
            <p className="font-display font-bold">{money(item.line_total)}</p>
            {item.domains_total > 0 && (
              <p className="text-[11px] text-muted-foreground">+ {money(item.domains_total)} domains</p>
            )}
          </div>

          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-muted-foreground hover:text-destructive"
            disabled={busy}
            onClick={onRemove}
            aria-label="Remove item"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function money(n: number) {
  return `$${n.toFixed(2)}`;
}
